import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, type ReactNode } from 'react'
import { DEAL_STATUSES } from '../../../../shared/schemas/corrections'
import type { AbschlussPatch, CreateDeal, Dismiss, ManualPayment, RefundDecision, ZahlungPatch } from '../../../../shared/schemas/corrections'
import type { AbschlussZeile, ProduktMitBegriffen, PruefenEintrag, TeamZeile } from '../../../api'
import { api } from '../../../api'
import { Betrag } from '../../../components/Betrag'
import { Feld } from '../../../components/Feld'
import { Info } from '../../../components/Info'
import { Knopf, type KnopfVariante } from '../../../components/Knopf'
import { Leiste } from '../../../components/Leiste'
import { Verweis } from '../../../components/Verweis'
import { Zustand } from '../../../components/Zustand'
import {
  AbschluesseVergleichenDialog, AbschlussAnlegenDialog, AbschlussProduktDialog, AbschlussStatusDialog, AbschlussZuweisenDialog, DatumSetzenDialog,
  ErledigenDialog, NettoBruttoDialog, ProduktZuweisenDialog, RolleZuweisenDialog, RueckzahlungDialog,
  ZahlungIgnorierenDialog, ZahlungVonHandDialog,
  type AbschlussBezug, type BeteiligterOption, type ProduktOption, type RolleFeld, type Vergleichsentscheidung,
} from '../../../components/korrekturen'
import { datumZeit } from '../../../format'
import { PRUEF_KINDS, pruefKind, type PruefAktion } from '../../../pruefkinds'
import type { SeitenProps } from '../../../screen'

export interface PruefSuche {
  art?: string
}

/** Beschriftung jedes Knopfes. Der Reihenfolge nach so, wie PRUEF_KINDS die Aktionen aufzählt. */
const AKTIONSTEXT: Readonly<Record<PruefAktion, string>> = {
  produkt_zuweisen: 'Produkt zuweisen',
  abschluss_zuweisen: 'Abschluss zuweisen',
  abschluss_anlegen: 'Closer zuordnen',
  zahlung_ignorieren: 'Zahlung ignorieren',
  datum_setzen: 'Bezahlt am setzen',
  netto_brutto: 'Netto/Brutto korrigieren',
  rueckzahlung: 'Rückzahlung entscheiden',
  zahlung_erfassen: 'Zahlung von Hand erfassen',
  rolle_zuweisen: 'Rolle zuweisen',
  abschluss_produkt: 'Produkt am Abschluss setzen',
  abschluss_status: 'Status ändern',
  abschluesse_vergleichen: 'Vergleichen',
  zur_rechnung: 'Zur Abrechnung',
  zu_den_regeln: 'Zu den Regeln',
  zu_den_einstellungen: 'Zu den Einstellungen',
  erledigen: 'Erledigt',
}

/** Welche Spalte am Abschluss die fehlende Rolle trägt. Nur Rollen, die aus einer Abschluss-Rolle kommen. */
const ROLLE: Readonly<Record<string, { feld: RolleFeld; name: string } | undefined>> = {
  CLOSER: { feld: 'closer_tc_id', name: 'Closer' },
  SETTER: { feld: 'setter_tc_id', name: 'Setter' },
  HOST: { feld: 'host_tc_id', name: 'Empfehlungsgeber' },
}

type DealStatus = (typeof DEAL_STATUSES)[number]

/**
 * `AbschlussZeile.status` ist in der API-Beschreibung ein `string` — die Liste gibt die Spalte weiter, wie sie
 * in der Datenbank steht. Der Dialog will einen der sechs bekannten Werte. Statt einer Behauptung (`as`) wird
 * hier nachgesehen; was nicht dabei ist, steht ohnehin auf „prüfen“.
 */
function alsStatus(wert: string): DealStatus {
  return DEAL_STATUSES.find((s) => s === wert) ?? 'pruefen'
}

type Ziel =
  | { art: 'produkt'; cinId: string }
  | { art: 'abschluss'; cinId: string; vorgabe: string | null }
  | { art: 'anlegen'; cinId: string }
  | { art: 'ignorieren'; cinId: string }
  | { art: 'datum'; cinId: string; deltaId: string }
  | { art: 'netto'; cinId: string; deltaId: string }
  | { art: 'refund'; cinId: string; deltaId: string; entscheidung: 'confirm' | 'ignore' }
  | { art: 'erfassen' }
  | { art: 'rolle'; abschluss: AbschlussBezug; feld: RolleFeld; rolleName: string; vorgabe: string | null }
  | { art: 'dealprodukt'; abschluss: AbschlussBezug; vorgabe: string | null }
  | { art: 'dealstatus'; abschluss: AbschlussBezug; vorgabe: DealStatus }
  | { art: 'vergleich'; abschluesse: AbschlussZeile[] }
  | { art: 'erledigen' }

interface Offen { item: PruefenEintrag; ziel: Ziel }

interface Auftrag { schluessel: string; meldung: string; ruf: () => Promise<unknown> }

function schluessel(item: PruefenEintrag): string {
  return `${item.kind}|${item.subject_id}`
}

/**
 * Was die Notiz einer Zeile ist, hängt an ihrer Quelle (services/queue.ts, CONTEXT_SQL): bei einer Rate
 * oder Zahlung der Rechnungsname, bei einer Provision der Rollenname, bei einem Produkt Kürzel und
 * Name. Das sind Fakten und stehen in der Liste. Alles andere — die Begründung des Abgleichs, ein
 * Fehlertext — ist Erklärung und steht hinter dem i.
 */
const NOTIZ_ALS_FAKT: Readonly<Record<string, string | undefined>> = {
  payment_delta: 'Rechnungsname',
  payment_event: 'Rechnungsname',
  commission: 'Rolle',
  product: 'Produkt',
}

/** Wie ein Knopf der Prüfliste aussieht: die erste Aktion trägt die Entscheidung, Ignorieren warnt. */
function varianteFuer(aktion: PruefAktion, index: number): KnopfVariante {
  if (aktion === 'zahlung_ignorieren') return 'warnung'
  return index === 0 ? 'primaer' : 'zweit'
}

function bezug(item: PruefenEintrag): string {
  return [item.customer_name, item.invoice_number, item.deal_key].filter((t) => t !== null && t !== '').join(' · ')
}

export function PruefenSeite({ tenantId, suche, setzeSuche, gehZu }: SeitenProps<PruefSuche>): ReactNode {
  const qc = useQueryClient()
  const [offen, setOffen] = useState<Offen | null>(null)
  const [erledigt, setErledigt] = useState<ReadonlySet<string>>(new Set())
  const [meldung, setMeldung] = useState('')

  const inbox = useQuery({
    queryKey: ['inbox', tenantId, suche.art ?? ''],
    queryFn: () => api.get<{ items: PruefenEintrag[] }>(`/api/t/${tenantId}/inbox`, { kind: suche.art }),
  })
  const items = inbox.data?.items ?? []
  const aktionen = new Set(items.flatMap((i) => pruefKind(i.kind).aktionen))

  // Die Kennung, die eine Korrektur in ihrem Pfad braucht, steht seit Part 24 Task 76b an der
  // Warteschlangenzeile selbst (`subject_parent_id`, `role_code`). Sie wird NICHT über
  // `invoice_number` in `/zahlungen` nachgeschlagen: diese Liste bricht bei LIST_CAP = 5000 still
  // ab, und eine Korrektur auf der falschen Kundenrechnung ist genau der Schaden, den dieser
  // Bildschirm verhindern soll.
  //
  // Geladen werden nur noch die Listen, aus denen ein Dialog seine Auswahl füllt — und der
  // Abschluss selbst, weil `PATCH /abschluesse/:id` seine `version` verlangt.
  const abschluesse = useQuery({
    queryKey: ['abschluesse', tenantId],
    queryFn: () => api.get<{ abschluesse: AbschlussZeile[] }>(`/api/t/${tenantId}/abschluesse`),
    enabled: aktionen.has('abschluss_zuweisen') || items.some((i) => i.subject_type === 'deal' || i.subject_type === 'commission'),
  })
  const produkte = useQuery({
    queryKey: ['produkte', tenantId],
    queryFn: () => api.get<{ produkte: ProduktMitBegriffen[] }>(`/api/t/${tenantId}/produkte`),
    enabled: aktionen.has('produkt_zuweisen') || aktionen.has('abschluss_anlegen') || aktionen.has('abschluss_produkt'),
  })
  const team = useQuery({
    queryKey: ['team', tenantId],
    queryFn: () => api.get<{ team: TeamZeile[] }>(`/api/t/${tenantId}/team`),
    enabled: aktionen.has('rolle_zuweisen') || aktionen.has('abschluss_anlegen') || aktionen.has('abschluesse_vergleichen'),
  })

  const abschlussNachId = new Map((abschluesse.data?.abschluesse ?? []).map((a) => [a.id, a]))

  const produktOptionen: ProduktOption[] = (produkte.data?.produkte ?? [])
    .filter((p) => p.active === 1)
    .map((p) => ({ id: p.id, code: p.code, name: p.name }))
  const beteiligte: BeteiligterOption[] = (team.data?.team ?? [])
    .filter((t) => t.active === 1)
    .map((t) => ({ id: t.tenant_contractor_id, bezeichnung: `${t.display_name} (${t.short_code})` }))
  const abschlussOptionen = (abschluesse.data?.abschluesse ?? []).map((a) => ({
    id: a.id,
    bezeichnung: `${a.customer_name} · ${a.product_code ?? 'ohne Produkt'} · ${a.status}`,
  }))

  const korrektur = useMutation<Auftrag, Error, Auftrag>({
    mutationFn: async (auftrag) => {
      await auftrag.ruf()
      return auftrag
    },
    onSuccess: (auftrag) => {
      setErledigt((alt) => new Set(alt).add(auftrag.schluessel))
      setMeldung(auftrag.meldung)
      setOffen(null)
      // Nie den eigenen Zwischenstand raten: jede betroffene Liste wird neu geholt (Vertrag Teil 4).
      for (const schl of ['inbox', 'zahlungen', 'abschluesse', 'provisionen', 'dashboard']) {
        void qc.invalidateQueries({ queryKey: [schl, tenantId] })
      }
    },
  })

  function starte(item: PruefenEintrag, meldungstext: string, ruf: () => Promise<unknown>): void {
    korrektur.mutate({ schluessel: schluessel(item), meldung: meldungstext, ruf })
  }

  /**
   * Jeder Weg in einen Dialog führt hier durch: der Fehler der vorigen Korrektur wird verworfen, bevor der
   * nächste Dialog aufgeht. Sonst stünde die Servermeldung der einen Zeile über dem Formular der nächsten.
   */
  function oeffne(item: PruefenEintrag, ziel: Ziel): void {
    korrektur.reset()
    setOffen({ item, ziel })
  }

  /** Was diese Zeile für eine Aktion braucht — oder null, wenn es sich aus den vorhandenen Listen nicht ergibt. */
  function zielFuer(item: PruefenEintrag, aktion: PruefAktion): Ziel | null {
    // Alles aus der Warteschlangenzeile selbst. Eine Kundenrechnungs-Zeile IST ihr eigenes Ziel
    // (`subject_id`); `subject_parent_id` ist dort NULL, weil die Zeile schon das Elternteil ist
    // (src/worker/services/queue.ts). Nur eine Rate trägt die customer_invoice_id im Elternfeld,
    // und eine Provisionszeile die deal_id.
    const cinId =
      item.subject_type === 'customer_invoice'
        ? item.subject_id
        : item.subject_type === 'payment_delta'
          ? item.subject_parent_id
          : null
    const deltaId = item.subject_type === 'payment_delta' ? item.subject_id : null
    const dealId =
      item.subject_type === 'deal'
        ? item.subject_id
        : item.subject_type === 'commission'
          ? item.subject_parent_id
          : null
    const deal = dealId === null ? null : abschlussNachId.get(dealId) ?? null
    const bezugAufAbschluss: AbschlussBezug | null = deal === null ? null : { id: deal.id, version: deal.version }

    switch (aktion) {
      case 'produkt_zuweisen': return cinId === null ? null : { art: 'produkt', cinId }
      case 'abschluss_zuweisen': return cinId === null ? null : { art: 'abschluss', cinId, vorgabe: null }
      case 'abschluss_anlegen': return cinId === null ? null : { art: 'anlegen', cinId }
      case 'zahlung_ignorieren': return cinId === null ? null : { art: 'ignorieren', cinId }
      case 'datum_setzen': return cinId === null || deltaId === null ? null : { art: 'datum', cinId, deltaId }
      case 'netto_brutto': return cinId === null || deltaId === null ? null : { art: 'netto', cinId, deltaId }
      case 'rueckzahlung': return cinId === null || deltaId === null ? null : { art: 'refund', cinId, deltaId, entscheidung: 'confirm' }
      case 'zahlung_erfassen': return { art: 'erfassen' }
      case 'rolle_zuweisen': {
        // Ein Code ausserhalb von Closer/Setter/Empfehlungsgeber ist die vierte, frei benannte Rolle;
        // ihr Name steht in der Zeile (`note` traegt das role_label der Provision).
        const rolle = item.role_code === null
          ? undefined
          : ROLLE[item.role_code] ?? { feld: 'sonstige_tc_id' as RolleFeld, name: item.note ?? 'Sonstige Rolle' }
        if (rolle === undefined || bezugAufAbschluss === null || deal === null) return null
        const vorgabe = deal[rolle.feld]
        return { art: 'rolle', abschluss: bezugAufAbschluss, feld: rolle.feld, rolleName: rolle.name, vorgabe }
      }
      case 'abschluss_produkt':
        return bezugAufAbschluss === null || deal === null ? null : { art: 'dealprodukt', abschluss: bezugAufAbschluss, vorgabe: deal.product_id }
      case 'abschluss_status':
        return bezugAufAbschluss === null || deal === null ? null : { art: 'dealstatus', abschluss: bezugAufAbschluss, vorgabe: alsStatus(deal.status) }
      case 'abschluesse_vergleichen': {
        // Die Geschwister: dieselbe Kombination aus Kontakt und Produkt, ebenfalls aktiv — genau die Bedingung,
        // unter der v_review_queue die Zeile erzeugt (match_key = Kontakt-Kürzel, migrations/0001_init.sql).
        if (deal === null) return null
        const geschwister = (abschluesse.data?.abschluesse ?? []).filter((a) =>
          a.id !== deal.id && a.hl_contact_id === deal.hl_contact_id && a.product_id === deal.product_id
          && (a.status === 'aktiv' || a.status === 'weiterlauf'))
        return geschwister.length === 0 ? null : { art: 'vergleich', abschluesse: [deal, ...geschwister] }
      }
      default: return { art: 'erledigen' }
    }
  }

  return (
    <div className="seite">
      <h1>Prüfen</h1>
      <p role="status" aria-live="polite" className="meldung">{meldung}</p>

      {/*
        Der Filter steht über dem Ladezustand, nicht dahinter: wer die Art umstellt, wartet sonst bei
        jedem Wechsel darauf, dass die Liste zurückkommt, bevor er die Auswahl wieder erreicht.
      */}
      <Leiste beschriftung="Prüfliste filtern">
        <Feld label="Art" htmlFor="art">
          <select
            id="art"
            value={suche.art ?? ''}
            onChange={(e) => { setzeSuche({ art: e.target.value === '' ? undefined : e.target.value }) }}
          >
            <option value="">alle Arten</option>
            {Object.keys(PRUEF_KINDS).map((k) => <option key={k} value={k}>{pruefKind(k).titel}</option>)}
          </select>
        </Feld>
        {inbox.isSuccess ? <p>{items.length} offene Fälle</p> : null}
      </Leiste>

      {inbox.isPending ? (
        <Zustand art="laedt" beschriftung="Die Prüfliste wird geladen." />
      ) : inbox.isError ? (
        <Zustand art="fehler" fehler={inbox.error} onWiederholen={() => { void inbox.refetch() }} />
      ) : items.length === 0 ? (
        <Zustand
          art="leer"
          titel="Nichts zu prüfen"
          hinweis="Jede Lieferung ist einem Abschluss und einem Produkt zugeordnet. Neue Einträge erscheinen hier automatisch, sobald etwas geliefert wird, das nicht eindeutig ist."
        />
      ) : (
        <ol className="pruefliste">
          {items.map((item) => {
            const text = pruefKind(item.kind)
            const id = schluessel(item)
            const istErledigt = erledigt.has(id)
            const knoepfe = text.aktionen.filter((a) => a !== 'erledigen')
            const notizFakt = NOTIZ_ALS_FAKT[item.subject_type]
            const notizAlsInfo = notizFakt === undefined && item.note !== null && item.note !== ''
            return (
              <li
                key={id}
                className={istErledigt ? 'pruefzeile zeile-erledigt' : 'pruefzeile'}
                data-erledigt={istErledigt ? 'true' : undefined}
              >
                {/*
                  Der Artikel trägt bewusst KEIN aria-labelledby: die Überschrift steht als erstes
                  Kind darin und wird ohnehin vorgelesen. Ein benannter Bereich wäre zusätzlich ein
                  Element, dessen zugänglicher Name der Zeilentitel ist — und dieser Titel kollidiert
                  mit den Feldern des Dialogs, den dieselbe Zeile öffnet („Netto/Brutto unplausibel“
                  gegen „Netto der Kundenrechnung“).
                */}
                <article className="pruefkarte">
                  <div className="pruef-inhalt">
                    {/* Der Titel allein sagt, was los ist. Satz, Folgen und die Begründung des
                        Servers stehen hinter dem i — sie wiederholen sich bei jeder Zeile derselben
                        Art und lenken beim Abarbeiten nur ab (Bastian, 11.10.2026). */}
                    <div className="pruef-titel">
                      <h2>{item.label}</h2>
                      <Info titel={item.label}>
                        <span>{text.satz}</span>
                        <span className="info-leise">{text.kosten}</span>
                        {notizAlsInfo ? <span className="info-leise">Hinweis des Systems: {item.note}</span> : null}
                      </Info>
                    </div>
                    <dl>
                      {/* Bei einer `contractor_invoice`-Zeile — heute nur `rechnung_rueckbuchung` —
                          trägt `customer_name` den Auftragnehmer und `invoice_number` seine
                          Rechnungsnummer (services/queue.ts, CONTEXT_SQL). „Kunde: Pia Beispiel" über
                          der Abrechnung von Pia Beispiel wäre schlicht falsch. */}
                      {item.customer_name === null ? null : (
                        <>
                          <dt>{item.subject_type === 'contractor_invoice' ? 'Auftragnehmer' : 'Kunde'}</dt>
                          <dd>{item.customer_name}</dd>
                        </>
                      )}
                      {item.invoice_number === null ? null : (
                        <>
                          <dt>{item.subject_type === 'contractor_invoice' ? 'Abrechnung' : 'Zahlung'}</dt>
                          <dd className="mono">{item.invoice_number}</dd>
                        </>
                      )}
                      {notizFakt === undefined || item.note === null || item.note === '' ? null : (
                        <><dt>{notizFakt}</dt><dd>{item.note}</dd></>
                      )}
                      {item.deal_key === null ? null : <><dt>Abschluss</dt><dd className="mono">{item.deal_key}</dd></>}
                      {text.betragLabel === null || item.amount_cents === null ? null : (
                        <><dt>{text.betragLabel}</dt><dd><Betrag cents={item.amount_cents} /></dd></>
                      )}
                      <dt>Offen seit</dt><dd>{datumZeit(item.since)}</dd>
                    </dl>
                  </div>
                  <div className="pruef-aktionen">
                    {knoepfe.map((aktion, index) => {
                      const verweisKlasse = index === 0 ? 'knopf ist-primaer' : 'knopf'
                      if (aktion === 'zur_rechnung') {
                        return <Verweis key={aktion} className={verweisKlasse} href={`/t/${tenantId}/rechnungen?rechnung=${item.subject_id}`} gehZu={gehZu}>{AKTIONSTEXT[aktion]}</Verweis>
                      }
                      if (aktion === 'zu_den_regeln') {
                        return <Verweis key={aktion} className={verweisKlasse} href={`/t/${tenantId}/regeln`} gehZu={gehZu}>{AKTIONSTEXT[aktion]}</Verweis>
                      }
                      if (aktion === 'zu_den_einstellungen') {
                        return <Verweis key={aktion} className={verweisKlasse} href={`/t/${tenantId}/einstellungen`} gehZu={gehZu}>{AKTIONSTEXT[aktion]}</Verweis>
                      }
                      const ziel = zielFuer(item, aktion)
                      if (ziel === null) {
                        return (
                          <p key={aktion} className="hinweis">
                            „{AKTIONSTEXT[aktion]}“ ist hier nicht möglich: der zugehörige Datensatz ist in den
                            geladenen Listen nicht enthalten.
                          </p>
                        )
                      }
                      if (ziel.art === 'refund') {
                        return (
                          <span key={aktion} className="pruef-aktionen-gruppe">
                            <Knopf onClick={() => { oeffne(item, { ...ziel, entscheidung: 'confirm' }) }}>Rückzahlung bestätigen</Knopf>
                            <Knopf variante="warnung" onClick={() => { oeffne(item, { ...ziel, entscheidung: 'ignore' }) }}>Rückzahlung ignorieren</Knopf>
                          </span>
                        )
                      }
                      return <Knopf key={aktion} variante={varianteFuer(aktion, index)} onClick={() => { oeffne(item, ziel) }}>{AKTIONSTEXT[aktion]}</Knopf>
                    })}
                    <Knopf variante="still" onClick={() => { oeffne(item, { art: 'erledigen' }) }}>Erledigt</Knopf>
                  </div>
                </article>
              </li>
            )
          })}
        </ol>
      )}

      {offen === null ? null : <Dialoge
        offen={offen}
        tenantId={tenantId}
        laeuft={korrektur.isPending}
        fehler={korrektur.error?.message ?? null}
        produkte={produktOptionen}
        beteiligte={beteiligte}
        abschluesse={abschlussOptionen}
        namen={new Map((team.data?.team ?? []).map((t) => [t.tenant_contractor_id, t.display_name]))}
        onSchliessen={() => { korrektur.reset(); setOffen(null) }}
        starte={starte}
      />}
    </div>
  )
}

interface DialogeProps {
  offen: Offen
  tenantId: string
  laeuft: boolean
  fehler: string | null
  produkte: ProduktOption[]
  beteiligte: BeteiligterOption[]
  abschluesse: Array<{ id: string; bezeichnung: string }>
  namen: ReadonlyMap<string, string>
  onSchliessen: () => void
  starte: (item: PruefenEintrag, meldung: string, ruf: () => Promise<unknown>) => void
}

/**
 * Ein Dialog je Ziel. Jeder ruft genau einen bestehenden Endpunkt; die Meldung sagt hinterher, was geschehen
 * ist, und der Erfolg blendet die Zeile aus.
 */
function Dialoge(p: DialogeProps): ReactNode {
  const { item, ziel } = p.offen
  const rahmen = { offen: true, laeuft: p.laeuft, fehler: p.fehler, onSchliessen: p.onSchliessen }
  const erklaerung = <p><strong>{item.label}</strong> — {bezug(item)}</p>
  const zahlungsPfad = (cinId: string): string => `/api/t/${p.tenantId}/zahlungen/${cinId}`

  switch (ziel.art) {
    case 'produkt':
      return <ProduktZuweisenDialog
        {...rahmen} erklaerung={erklaerung} produkte={p.produkte} vorgabe={null}
        onAbsenden={(koerper: ZahlungPatch) => {
          p.starte(item, 'Produkt zugewiesen.', () => api.patch(zahlungsPfad(ziel.cinId), koerper))
        }}
      />
    case 'abschluss':
      return <AbschlussZuweisenDialog
        {...rahmen} erklaerung={erklaerung} abschluesse={p.abschluesse} vorgabe={ziel.vorgabe}
        onAbsenden={(koerper: ZahlungPatch) => {
          p.starte(item, 'Abschluss zugewiesen.', () => api.patch(zahlungsPfad(ziel.cinId), koerper))
        }}
      />
    case 'ignorieren':
      return <ZahlungIgnorierenDialog
        {...rahmen} erklaerung={erklaerung} bereitsIgnoriert={false}
        onAbsenden={(koerper: ZahlungPatch) => {
          p.starte(item, 'Zahlung ignoriert.', () => api.patch(zahlungsPfad(ziel.cinId), koerper))
        }}
      />
    case 'datum':
      return <DatumSetzenDialog
        {...rahmen} erklaerung={erklaerung} deltaId={ziel.deltaId}
        onAbsenden={(koerper: ZahlungPatch) => {
          p.starte(item, 'Zahlungseingang gesetzt.', () => api.patch(zahlungsPfad(ziel.cinId), koerper))
        }}
      />
    case 'netto':
      return <NettoBruttoDialog
        {...rahmen} erklaerung={erklaerung} deltaId={ziel.deltaId} vorgabeNetto={null} vorgabeBrutto={null}
        onAbsenden={(koerper: ZahlungPatch) => {
          p.starte(item, 'Netto und Brutto korrigiert.', () => api.patch(zahlungsPfad(ziel.cinId), koerper))
        }}
      />
    case 'refund':
      return <RueckzahlungDialog
        {...rahmen} erklaerung={erklaerung} entscheidung={ziel.entscheidung}
        onAbsenden={(koerper: RefundDecision) => {
          const meldung = ziel.entscheidung === 'confirm' ? 'Rückzahlung bestätigt.' : 'Rückzahlung ignoriert.'
          p.starte(item, meldung, () => api.post(
            `${zahlungsPfad(ziel.cinId)}/refund/${ziel.deltaId}/${ziel.entscheidung}`, koerper,
          ))
        }}
      />
    case 'anlegen':
      return <AbschlussAnlegenDialog
        {...rahmen} erklaerung={erklaerung} produkte={p.produkte} beteiligte={p.beteiligte} vorgabeProduktId={item.product_id ?? null}
        onAbsenden={(koerper: CreateDeal) => {
          p.starte(item, 'Closer zugeordnet — die Provision ist berechnet.', () => api.post(
            `${zahlungsPfad(ziel.cinId)}/create-deal`, koerper,
          ))
        }}
      />
    case 'erfassen':
      return <ZahlungVonHandDialog
        {...rahmen}
        erklaerung={erklaerung}
        vorgabe={{
          invoice_number: item.invoice_number ?? '',
          customer_name: item.customer_name ?? '',
          invoice_name: item.note ?? '',
          hl_contact_id: null,
          cumulative_paid_cents: item.amount_cents,
          // Nur eine fehlgeschlagene Lieferung darf abgelöst werden; bei einer nicht lesbaren Zahlung ist das
          // Subjekt das Zahlungs-Ereignis, nicht die Lieferung — dann bleibt die Verknüpfung leer.
          source_webhook_event_id: item.subject_type === 'webhook_event' ? item.subject_id : null,
        }}
        onAbsenden={(koerper: ManualPayment) => {
          p.starte(item, 'Zahlung erfasst.', () => api.post(`/api/t/${p.tenantId}/zahlungen/manuell`, koerper))
        }}
      />
    case 'rolle':
      return <RolleZuweisenDialog
        {...rahmen} erklaerung={erklaerung} feld={ziel.feld} rolleName={ziel.rolleName}
        beteiligte={p.beteiligte} vorgabe={ziel.vorgabe} abschluss={ziel.abschluss}
        onAbsenden={(koerper: AbschlussPatch) => {
          p.starte(item, `${ziel.rolleName} zugewiesen.`, () => api.patch(
            `/api/t/${p.tenantId}/abschluesse/${ziel.abschluss.id}`, koerper,
          ))
        }}
      />
    case 'dealprodukt':
      return <AbschlussProduktDialog
        {...rahmen} erklaerung={erklaerung} produkte={p.produkte} vorgabe={ziel.vorgabe} abschluss={ziel.abschluss}
        onAbsenden={(koerper: AbschlussPatch) => {
          p.starte(item, 'Produkt am Abschluss gesetzt.', () => api.patch(
            `/api/t/${p.tenantId}/abschluesse/${ziel.abschluss.id}`, koerper,
          ))
        }}
      />
    case 'vergleich':
      return <AbschluesseVergleichenDialog
        {...rahmen} erklaerung={erklaerung} abschluesse={ziel.abschluesse} namen={p.namen}
        onAbsenden={(e: Vergleichsentscheidung) => {
          if (e.art === 'alle') {
            p.starte(item, 'Alle Abschlüsse bleiben; der Hinweis ist abgehakt.', async () => {
              for (const a of ziel.abschluesse) {
                await api.post(`/api/t/${p.tenantId}/inbox/doppelter_abschluss/${encodeURIComponent(a.id)}/dismiss`, { note: e.grund })
              }
            })
            return
          }
          const name = e.behalten.hl_deal_key ?? e.behalten.customer_name
          p.starte(item, `Zusammengeführt — ${name} bleibt, die Zahlungen des anderen rechnen jetzt dorthin.`, async () => {
            for (const a of e.streichen) {
              await api.post(`/api/t/${p.tenantId}/abschluesse/${encodeURIComponent(e.behalten.id)}/zusammenfuehren`, {
                streichen: a.id, version: a.version, reason: e.grund,
              })
            }
          })
        }}
      />
    case 'dealstatus':
      return <AbschlussStatusDialog
        {...rahmen} erklaerung={erklaerung} vorgabe={ziel.vorgabe} abschluss={ziel.abschluss}
        onAbsenden={(koerper: AbschlussPatch) => {
          p.starte(item, 'Status geändert.', () => api.patch(
            `/api/t/${p.tenantId}/abschluesse/${ziel.abschluss.id}`, koerper,
          ))
        }}
      />
    default:
      return <ErledigenDialog
        {...rahmen} erklaerung={erklaerung}
        onAbsenden={(koerper: Dismiss) => {
          p.starte(item, 'Als erledigt abgehakt.', () => api.post(
            `/api/t/${p.tenantId}/inbox/${encodeURIComponent(item.kind)}/${encodeURIComponent(item.subject_id)}/dismiss`,
            koerper,
          ))
        }}
      />
  }
}

export const Route = createFileRoute('/t/$tenantId/pruefen')({
  validateSearch: (roh: Record<string, unknown>): PruefSuche => {
    const art = roh['art']
    return typeof art === 'string' && art !== '' ? { art } : {}
  },
  component: PruefenRoute,
})

function PruefenRoute(): ReactNode {
  const { tenantId } = Route.useParams()
  const suche = Route.useSearch()
  const navigate = useNavigate()
  return (
    <PruefenSeite
      tenantId={tenantId}
      suche={suche}
      setzeSuche={(naechste) => { void navigate({ to: '.', search: (alt: PruefSuche) => ({ ...alt, ...naechste }) }) }}
      gehZu={(pfad) => { void navigate({ to: pfad }) }}
    />
  )
}
